import { FC } from "react";
import styles from "./index.module.scss";
import ReceiptRow from "./ReceiptRow";
import { formatMoney } from "@/helpers/numbers";
import { useTranslation } from "react-i18next";
import ContentContainer from "@/components/content-container";
import type { Discount } from "@/types/discount";
import type { ReceiptProduct } from "@/types/receipt";
import classNames from "classnames";
import Bag from "@/assets/images/Bag";
import { pickupTimeShort } from "@/helpers/pickup";
import useCashRegisterStore from "@/stores/cash-register";

interface ReceiptProps {
  products: ReceiptProduct[];
  discounts: Discount[];
  productTotalAmountCents: number;
  amountCents: number;
  pickupStartAt?: string;
  depositTotalAmountCents?: number;
  vatCents?: number;
  className?: string;
  successPage?: boolean;
}

const Receipt: FC<ReceiptProps> = ({
  products,
  discounts,
  productTotalAmountCents,
  amountCents,
  pickupStartAt,
  depositTotalAmountCents,
  vatCents,
  successPage = false,
  className,
}) => {
  const { t } = useTranslation();
  const address = useCashRegisterStore().getAddress();

  return (
    <ContentContainer
      title={t("components.receipt.yourReceipt")}
      className={classNames(className)}
      belowTitle={
        pickupStartAt ? (
          <div className={successPage ? styles.pickupInfoSuccess : styles.pickupInfo}>
            <div className={styles.pickStartAtRow}>
              <Bag fill={undefined} />
              <span>{pickupTimeShort(pickupStartAt)}</span>
            </div>
            {address && <span>{address}</span>}
          </div>
        ) : undefined
      }
      success={successPage}>
      <div className={styles.receiptDetails}>
        {products.map((item) => (
          <ReceiptRow key={item.productId} {...item} />
        ))}
        {discounts.length > 0 && (
          <div className={styles.subtotalContainer}>
            <b>{t("components.receipt.subtotal")}</b>
            <b>{formatMoney(productTotalAmountCents / 100)}</b>
          </div>
        )}
        {discounts.map((discount, index) => (
          <ReceiptRow key={index} {...discount} />
        ))}
      </div>
      <div className={styles.bottomRows}>
        <div className={successPage ? styles.totalContainerSuccess : styles.totalContainer}>
          <b>{t("ui.total")}</b>
          <b data-cy="receipt-total">{formatMoney(amountCents / 100)}</b>
        </div>
        {depositTotalAmountCents !== undefined && depositTotalAmountCents > 0 && (
          <div className={styles.extraPriceContainer}>
            <span>{t("components.receipt.deposit")}</span>
            <b data-cy="deposit-total">{formatMoney(depositTotalAmountCents / 100)}</b>
          </div>
        )}
        {vatCents !== undefined && (
          <div className={styles.extraPriceContainer}>
            <span>{t("components.receipt.vat")}</span>
            <b>{formatMoney(vatCents / 100)}</b>
          </div>
        )}
      </div>
    </ContentContainer>
  );
};

export default Receipt;
